import React, { useEffect, useState } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { Job } from '../../lib/api';
import { getContractorRecommendations, RecommendationScore } from '../../lib/recommendations';
import { TradeBadge } from '../shared/TradeBadge';
import {
  Sparkles,
  Star,
  MapPin,
  Briefcase,
  CheckCircle2,
  ChevronRight,
  Lightbulb,
  X
} from 'lucide-react';

interface RecommendedContractorsProps {
  job: Job;
  onViewProfile: (contractorId: string) => void;
  onClose?: () => void;
}

export const RecommendedContractors: React.FC<RecommendedContractorsProps> = ({
  job,
  onViewProfile,
  onClose,
}) => {
  const [recommendations, setRecommendations] = useState<RecommendationScore[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadRecommendations();
  }, [job.id]);

  const loadRecommendations = async () => {
    setLoading(true);
    try {
      const results = await getContractorRecommendations(job, 5);
      setRecommendations(results);
    } catch (error) {
      console.error('Error loading recommendations:', error);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="bg-white rounded-xl border border-slate-200 p-6">
        <div className="flex items-center justify-center h-32">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-slate-900"></div>
        </div>
      </div>
    );
  }

  if (recommendations.length === 0) {
    return null;
  }

  return (
    <div className="bg-gradient-to-br from-amber-50 to-slate-50 rounded-xl border border-amber-200 p-6">
      <div className="flex items-start justify-between mb-4">
        <div className="flex items-center space-x-3">
          <div className="w-10 h-10 rounded-lg bg-amber-100 flex items-center justify-center">
            <Sparkles className="w-5 h-5 text-amber-600" />
          </div>
          <div>
            <h3 className="font-bold text-slate-900 flex items-center">
              Recommended Contractors
              <span className="ml-2 text-xs bg-amber-100 text-amber-700 px-2 py-0.5 rounded-full">
                {recommendations.length}
              </span>
            </h3>
            <p className="text-sm text-slate-600">Best matches for {job.title}</p>
          </div>
        </div>
        {onClose && (
          <button
            onClick={onClose}
            className="text-slate-400 hover:text-slate-600 transition-colors"
          >
            <X className="w-5 h-5" />
          </button>
        )}
      </div>

      <div className="space-y-3">
        {recommendations.map(({ contractor, score, reasons }, index) => (
          <div
            key={contractor.id}
            className="bg-white rounded-lg border border-slate-200 p-4 hover:shadow-md transition-all group"
          >
            <div className="flex items-start justify-between">
              <div className="flex items-start space-x-3 flex-1">
                <div className="relative">
                  <div className="w-12 h-12 rounded-full bg-gradient-to-br from-slate-100 to-slate-200 flex items-center justify-center flex-shrink-0">
                    <span className="text-lg font-semibold text-slate-700">
                      {contractor.full_name?.charAt(0) || '?'}
                    </span>
                  </div>
                  {index === 0 && (
                    <div className="absolute -top-1 -right-1 w-5 h-5 bg-amber-400 rounded-full flex items-center justify-center">
                      <span className="text-xs font-bold text-white">1</span>
                    </div>
                  )}
                </div>

                <div className="flex-1 min-w-0">
                  <div className="flex items-center space-x-2 mb-1">
                    <h4 className="font-bold text-slate-900 truncate">{contractor.full_name}</h4>
                    {contractor.verification_status === 'verified' && (
                      <CheckCircle2 className="w-4 h-4 text-green-600 flex-shrink-0" />
                    )}
                  </div>

                  <div className="flex flex-wrap gap-1.5 mb-2">
                    {contractor.trades && contractor.trades.length > 0 ? (
                      contractor.trades.slice(0, 2).map((trade) => (
                        <TradeBadge key={trade} trade={trade} size="sm" />
                      ))
                    ) : contractor.trade_type ? (
                      <TradeBadge trade={contractor.trade_type} size="sm" />
                    ) : null}
                  </div>

                  <div className="flex items-center space-x-3 text-xs text-slate-600 mb-2">
                    {contractor.location && (
                      <div className="flex items-center">
                        <MapPin className="w-3 h-3 mr-1" />
                        <span className="truncate">{contractor.location}</span>
                      </div>
                    )}
                    <div className="flex items-center">
                      <Star className="w-3 h-3 mr-1 text-amber-500" />
                      <span className="font-medium text-slate-900">{contractor.rating.toFixed(1)}</span>
                    </div>
                    <div className="flex items-center">
                      <Briefcase className="w-3 h-3 mr-1" />
                      <span>{contractor.jobs_completed} jobs</span>
                    </div>
                  </div>

                  {reasons.length > 0 && (
                    <div className="flex items-start space-x-1.5 text-xs">
                      <Lightbulb className="w-3 h-3 text-amber-600 flex-shrink-0 mt-0.5" />
                      <div className="flex flex-wrap gap-1">
                        {reasons.slice(0, 2).map((reason, idx) => (
                          <span key={idx} className="text-slate-600">
                            {reason}
                            {idx < Math.min(reasons.length - 1, 1) && ','}
                          </span>
                        ))}
                        {reasons.length > 2 && (
                          <span className="text-slate-500">+{reasons.length - 2} more</span>
                        )}
                      </div>
                    </div>
                  )}
                </div>
              </div>

              <button
                onClick={() => onViewProfile(contractor.id)}
                className="ml-2 px-3 py-1.5 bg-slate-900 hover:bg-slate-800 text-white text-sm font-medium rounded-lg transition-colors flex items-center opacity-0 group-hover:opacity-100"
              >
                View
                <ChevronRight className="w-4 h-4 ml-0.5" />
              </button>
            </div>
          </div>
        ))}
      </div>

      <div className="mt-4 pt-4 border-t border-amber-200">
        <p className="text-xs text-slate-600 flex items-center">
          <Sparkles className="w-3 h-3 mr-1 text-amber-600" />
          Recommendations based on trade specialty, ratings, location, and experience
        </p>
      </div>
    </div>
  );
};
